<template>
  <div class="form-wrapper">
    <van-nav-bar title="家教报名" />
    <van-form @submit="onSubmit" :validate-first="true">
      <van-cell-group inset>
        <!-- 省市区选择 -->
        <van-field
          required
          readonly
          clickable
          label="可接地区："
          :value="regionText"
          placeholder="请选择可接地区"
          @click="showArea = true"
          :rules="[{ required: true, message: '请选择可接地区' }]"
        />
        <van-popup v-model="showArea" position="bottom">
          <van-area
            :area-list="areaList"
            @confirm="onAreaConfirm"
            @cancel="showArea = false"
          />
        </van-popup>
        <!-- 其他表单项... -->
        <van-field required v-model="form.school" label="学校：" placeholder="请输入学校" :rules="[{ required: true, message: '请输入学校名称' }]" />
        <van-field required v-model="form.grade" label="年级：" placeholder="如：身份描述，大学一年级、研一,等等" :rules="[{ required: true, message: '请输入年级' }]" />
        <van-field required v-model="form.major" label="专业：" placeholder="请输入专业" :rules="[{ required: true, message: '请输入专业名称' }]" />
        <van-field required v-model="form.name" label="姓名：" placeholder="请输入姓名" :rules="[{ required: true, message: '请输入姓名' }]" />
        <van-field required label="性别：" :rules="[{ required: true, message: '请选择性别' }]">
          <template #input>
            <van-radio-group v-model="form.sex" direction="horizontal">
              <van-radio name="male">男</van-radio>
              <van-radio name="female">女</van-radio>
            </van-radio-group>
          </template>
        </van-field>
        <van-field v-model="form.mobile" label="手机号：" placeholder="请输入手机号" type="tel"
          :rules="[{ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', required: false }]" />
        <van-field v-model="form.wx" label="微信：" placeholder="请输入微信号" :rules="[]" />
        <!-- 可教科目多选+tag -->
        <van-field
          required
          label="可教科目："
          readonly
          clickable
          :value="''"
          @click="showSubjectPicker = true"
          :rules="[
            {
              validator: () => selectedSubjects.length > 0,
              message: '请选择可教科目'
            }
          ]"
        >
          <template #input>
            <div style="min-height: 32px;">
              <template v-if="selectedSubjects.length">
                <van-tag
                  v-for="(item, idx) in selectedSubjects"
                  :key="item"
                  type="primary"
                  closeable
                  size="medium"
                  style="margin-right: 4px; margin-bottom: 4px;"
                  @close="removeSubject(idx)"
                >
                  {{ item }}
                </van-tag>
              </template>
              <template v-else>
                <span style="color: #c8c9cc;">请选择可教科目（可多选）</span>
              </template>
            </div>
          </template>
        </van-field>
        <van-popup v-model="showSubjectPicker" position="bottom">
          <van-checkbox-group v-model="selectedSubjects">
            <van-cell-group>
              <van-cell
                v-for="item in subjectOptions"
                :key="item"
                clickable
                @click="toggleSubject(item)"
              >
                <template #title>
                  <van-checkbox :name="item">{{ item }}</van-checkbox>
                </template>
              </van-cell>
            </van-cell-group>
          </van-checkbox-group>
          <div style="padding: 16px;">
            <van-button type="info" block @click="showSubjectPicker = false">完成</van-button>
          </div>
        </van-popup>
        <van-field required v-model="form.desc" label="自我介绍：" type="textarea" placeholder="请输入自我介绍" rows="3" :rules="[{ required: true, message: '请输入自我介绍' }]" />
      </van-cell-group>
      <div style="margin: 24px 16px 0 16px;">
        <van-button round block type="info" native-type="submit">提交</van-button>
      </div>
    </van-form>
  </div>
</template>

<script>
import { getSubjects } from '@/api/tutoringAgency';
import { submitResume } from '@/api/tutoringAgency';
import { areaList } from '@vant/area-data';
import debounce from 'lodash.debounce';
import { handleAuthRequired } from '@/utils/wechatAuth';

export default {
  name: 'TutoringRegistration',
  props: {
    orgId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showArea: false,
      showSubjectPicker: false,
      form: {
        city: '', // 存储选中的地区code
        school: '',
        grade: '',
        major: '',
        name: '',
        sex: '',
        mobile: '',
        wx: '',
        teach_subject: '', // 英文逗号分隔的字符串
        desc: '',
        orgId: '' // 仅用于提交
      },
      selectedSubjects: [], // 用于多选临时存储
      regionText: '', // 展示选中的地区名称
      areaList,
      subjectOptions: []
    }
  },
  async created() {
    // 检查登录状态
    const isAuthenticated = await handleAuthRequired(this.$store, this.$route.fullPath);
    if (!isAuthenticated) {
      return; // 如果未登录，handleAuthRequired会自动跳转到授权页面
    }
    
    // 只从props获取orgId
    this.form.orgId = this.orgId;
    this.loadSubjects();
  },
  methods: {
    async loadSubjects() {
      try {
        const res = await getSubjects();
        this.subjectOptions = res.data;
      } catch (e) {
        this.$toast.fail('获取科目失败');
      }
    },
    // 省市区选择确认
    onAreaConfirm(values) {
      this.regionText = values.map(item => item.name).join(' ');
      this.form.city = values.map(item => item.name).join('/');
      this.showArea = false;
    },
    toggleSubject(item) {
      const idx = this.selectedSubjects.indexOf(item)
      if (idx > -1) {
        this.selectedSubjects.splice(idx, 1)
      } else {
        this.selectedSubjects.push(item)
      }
    },
    removeSubject(idx) {
      this.selectedSubjects.splice(idx, 1)
    },
    onSubmit: debounce(function() {
      // teach_subject直接为英文逗号分隔字符串
      this.form.teach_subject = this.selectedSubjects.join(',');
      const submitData = { ...this.form };
      submitResume(submitData)
        .then(() => {
          this.$toast.success('提交成功！');
          // 清空表单内容
          this.form = {
            city: '',
            school: '',
            grade: '',
            major: '',
            name: '',
            sex: '',
            mobile: '',
            wx: '',
            teach_subject: '',
            desc: '',
            orgId: this.orgId // 保持orgId不变
          };
          this.selectedSubjects = [];
          this.regionText = '';
        })
        .catch(() => {
          this.$toast.fail('提交失败，请重试');
        });
    }, 800)
  }
}
</script>

<style scoped>
.form-wrapper {
  min-height: 100vh;
  background: #f7f8fa;
}

.van-nav-bar {
  border-radius: 0 0 12px 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.van-cell-group {
  margin-top: 12px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}
</style> 